import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import AnimatedSection from './AnimatedSection';

const ServiceCard = ({ title, description, link, index }) => {
  const orderNumber = String(index + 1).padStart(2, '0');
  
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Link 
        to={link || '#'}
        className={`group block relative bg-[#F0EDE7] p-6 md:p-8 transition-all duration-300 ease-out hover:bg-[#1C1C1A] h-full ${!link ? 'pointer-events-none' : ''}`}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#B8975A] transform origin-right scale-x-0 group-hover:scale-x-100 group-hover:origin-left transition-transform duration-300 ease-out"></div>
        <span className="block font-serif text-sm text-[#B8975A]/40 mb-4 group-hover:text-[#B8975A]/60 transition-colors duration-300">
          {orderNumber}
        </span>
        <h4 className="font-serif text-xl md:text-2xl text-[#1C1C1A] mb-3 group-hover:text-white transition-colors duration-300">
          {title}
        </h4>
        <p className="font-sans text-sm text-[#4A4845] leading-relaxed group-hover:text-white/80 transition-colors duration-300">
          {description}
        </p>
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#B8975A]"></div>
      </Link>
    </motion.div>
  );
};

const CategorySection = ({ title, services, globalStartIndex, isFirst }) => {
  return (
    <div className={`mb-24 last:mb-0 ${!isFirst ? 'pt-20 mt-16 border-t border-[#B8975A]/20' : ''}`}>
      <AnimatedSection animation="fadeUp" className="flex items-center gap-8 mb-14">
        <h3 className="text-base md:text-lg lg:text-xl tracking-[0.35em] uppercase text-[#B8975A] font-semibold whitespace-nowrap">
          {title}
        </h3>
        <div className="flex-1 h-[2px] bg-[#B8975A]/40"></div>
      </AnimatedSection>
      
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }}
      >
        {services.map((service, index) => (
          <ServiceCard 
            key={index}
            title={service.title}
            description={service.description}
            link={service.link}
            index={globalStartIndex + index}
          />
        ))}
      </motion.div>
    </div>
  );
};

const Services = () => {
  const { t } = useTranslation();

  const categories = [
    {
      title: t('nav.facialSurgery'),
      services: [
        { title: t('services.blepharoplasty'), description: t('services.blepharoplastyDesc'), link: '/blefaroplastia' },
        { title: t('services.facelift'), description: t('services.faceliftDesc'), link: '/lifting-facial' },
        { title: t('services.rhinoplasty'), description: t('services.rhinoplastyDesc'), link: '/rinoplastia' },
        { title: t('services.otoplasty'), description: t('services.otoplastyDesc'), link: '/otoplastia' },
      ]
    },
    {
      title: t('nav.bodySurgery'),
      services: [
        { title: t('services.breastSurgery'), description: t('services.breastSurgeryDesc'), link: '/cirugia-mamas' },
        { title: t('services.gynecomastia'), description: t('services.gynecomastiaDesc'), link: '/ginecomastia' },
        { title: t('services.abdominoplasty'), description: t('services.abdominoplastyDesc'), link: '/abdominoplastia' },
        { title: t('services.postBariatric'), description: t('services.postBariatricDesc'), link: '/cirugia-postbariatrica' },
        { title: t('services.liposuction'), description: t('services.liposuctionDesc'), link: '/liposuccion' },
        { title: t('services.gynecologicalAesthetics'), description: t('services.gynecologicalAestheticsDesc'), link: '/gineco-estetica' },
      ]
    },
    {
      title: t('nav.aestheticMedicine'),
      services: [
        { title: t('services.rhinomodeling'), description: t('services.rhinomodelingDesc'), link: '/rinomodelacion' },
        { title: t('services.endolifting'), description: t('services.endoliftingDesc'), link: '/endolifting' },
        { title: t('services.jowlRemoval'), description: t('services.jowlRemovalDesc'), link: '/papada' },
        { title: t('services.browLift'), description: t('services.browLiftDesc'), link: '/elevacion-cejas' },
        { title: t('services.facialHarmonization'), description: t('services.facialHarmonizationDesc'), link: '/armonizacion-facial' },
        { title: t('services.darkCircles'), description: t('services.darkCirclesDesc'), link: '/ojeras-sin-cirugia' },
        { title: t('services.integrativeMedicine'), description: t('services.integrativeMedicineDesc'), link: '/medicina-integrativa' },
        { title: t('services.gynecologicalAesthetics'), description: t('services.gynecologicalAestheticsDesc'), link: '/gineco-estetica' },
      ]
    },
    {
      title: t('nav.reconstructiveSurgery'),
      services: [
        { title: t('services.dermatologicalSurgery'), description: t('services.dermatologicalSurgeryDesc'), link: '/cirugia-dermatologica' },
        { title: t('services.secondaryPlasticSurgery'), description: t('services.secondaryPlasticSurgeryDesc'), link: '/cirugias-secundarias' },
        { title: t('services.secondOpinion'), description: t('services.secondOpinionDesc'), link: '/segunda-opinion' },
        { title: t('services.medicalExpertise'), description: t('services.medicalExpertiseDesc'), link: '/peritacion-medica' },
      ]
    },
  ];

  let globalIndex = 0;
  const categoriesWithIndex = categories.map(category => {
    const startIndex = globalIndex;
    globalIndex += category.services.length;
    return { ...category, startIndex };
  });

  return (
    <section id="servicios" className="py-28 bg-[#FDFBF8]">
      <div className="container mx-auto px-6">
        <AnimatedSection animation="fadeUp" className="max-w-3xl mb-20">
          <p className="text-sm md:text-base tracking-[0.4em] uppercase text-[#B8975A] font-medium mb-8">
            {t('services.eyebrow')}
          </p>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1C1C1A] leading-tight">
            {t('services.title').split(' ').slice(0, 2).join(' ')}<br />
            <span className="italic text-[#B8975A]">{t('services.title').split(' ').slice(2).join(' ')}</span>
          </h2>
          <div className="w-20 h-[2px] bg-[#B8975A] mt-10"></div>
        </AnimatedSection>

        {categoriesWithIndex.map((category, index) => (
          <CategorySection 
            key={index}
            title={category.title}
            services={category.services}
            globalStartIndex={category.startIndex}
            isFirst={index === 0}
          />
        ))}
      </div>
    </section>
  );
};

export default Services;
